import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, map, of } from 'rxjs';
import { catchError, distinct, mergeMap } from 'rxjs/operators';
import * as actions from './actions';
import { TenantAjaxService } from 'projects/angular-clarity/src/app/services/ajax/tenant-ajax.service';

@Injectable()
export class GraphSPOSharePointSiteDetailsEffects {
    loadSites$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadSPOSiteUsage),
            distinct((action) => action._tenant),
            mergeMap((action) =>
                this.get(action._tenant).pipe(
                    map((data: any) => actions.loadSPOSiteUsageSuccess({ _tenant: action._tenant, data })),
                    catchError((error) => of(actions.loadSPOSiteUsageFailure({ _tenant: action._tenant, error }))),
                ),
            ),
        ),
    );

    get(_tenant: string) {
        const url = "api/microsoft/graph/reports/getSharePointSiteUsageDetail(period='D7')?$format=application/json";

        const sites$ = this.ajax.get(_tenant, url).pipe(
            map((res) => res.value),

            map((res) =>
                res.map((site) => ({
                    ...site,
                    storageUsedPercentage:
                        site.storageAllocatedInBytes !== 0
                            ? (site.storageUsedInBytes * 100) / site.storageAllocatedInBytes
                            : 0,

                    storageUsedInGBytes: (site.storageUsedInBytes / (1024 * 1024 * 1024)).toFixed(2),
                    storageAllocatedInGBytes: (site.storageAllocatedInBytes / (1024 * 1024 * 1024)).toFixed(2),
                })),
            ),
            map((res) => res.filter((res) => !res.isDeleted)),
        );

        // The tenant storage pool is only exposed through PowerShell. Fall back to the per-site
        // rows alone when it cannot be read, rather than failing the whole table.
        const spoTenant$ = this.ajax
            .get(_tenant, '/api/microsoft/powershell/spo/tenant')
            .pipe(catchError(() => of(null)));

        return forkJoin([sites$, spoTenant$]).pipe(
            map(([sites, spoTenant]) => {
                const storageQuotaInMB = spoTenant?.[0]?.StorageQuota;

                if (!storageQuotaInMB || storageQuotaInMB <= 0) return sites;

                const storageAllocatedInBytes = storageQuotaInMB * (1024 * 1024);
                const storageUsedInBytes = sites.reduce((total, site) => total + site.storageUsedInBytes, 0);

                const tenantRow = {
                    siteId: 'tenant-total',
                    siteUrl: 'All SharePoint sites (tenant total)',
                    ownerDisplayName: '',
                    rootWebTemplate: null,
                    isDeleted: false,
                    storageUsedInBytes,
                    storageAllocatedInBytes,
                    storageUsedPercentage: (storageUsedInBytes * 100) / storageAllocatedInBytes,
                    storageUsedInGBytes: (storageUsedInBytes / (1024 * 1024 * 1024)).toFixed(2),
                    storageAllocatedInGBytes: (storageAllocatedInBytes / (1024 * 1024 * 1024)).toFixed(2),
                };

                // Tenant row only, matching what the deviation check evaluates.
                return [tenantRow];
            }),
        );
    }

    constructor(
        private actions$: Actions,
        private ajax: TenantAjaxService,
    ) {}
}
